import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Payment } from "@/components/settings/admin/stats/payments/types";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, MoreVertical, FileText, Search, Pencil, ShoppingBag, X } from "lucide-react";
import { EditPaymentDialog } from "@/components/admin/EditPaymentDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { getPaymentDate } from "@/lib/paymentDate";

interface PaymentsTableProps {
  payments: Payment[];
  isLoading: boolean;
  gymId?: string | null;
}

type SortBy = "date_desc" | "date_asc" | "amount_desc" | "amount_asc";
const ARTICLE_SALE_KEY = "__article_sale__";

export const PaymentsTable = ({ payments, isLoading, gymId }: PaymentsTableProps) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [methodFilter, setMethodFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortBy>("date_desc");
  const pageSize = 20;

  useEffect(() => setCurrentPage(1), [payments, searchQuery, methodFilter, categoryFilter, sortBy]);


  const formatDate = (payment: Payment) => {
    return format(getPaymentDate(payment), "PPP", { locale: es });
  };

  const formatDateShort = (payment: Payment) => {
    return format(getPaymentDate(payment), "dd/MM/yy");
  };

  const getUserName = (payment: Payment) => {
    const profile = payment.profiles;
    // Walk-in / venta sin cliente: profiles viene null intencionalmente
    if (!profile) return payment.is_article_sale ? "—" : "Usuario desconocido";

    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }

    return profile.username?.split('@')[0] || "Usuario desconocido";
  };

  // Listas únicas para los selectores (sobre todos los pagos, no la página)
  const availableMethods = useMemo(() => {
    const set = new Set<string>();
    payments.forEach((p) => {
      if (p.payment_method) set.add(p.payment_method);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [payments]);

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    payments.forEach((p) => {
      if (p.is_article_sale) return;
      if (!p.description) return;
      p.description.split(", ").forEach((c) => {
        const trimmed = c.trim();
        if (trimmed) set.add(trimmed);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [payments]);

  const hasArticleSales = useMemo(
    () => payments.some((p) => p.is_article_sale),
    [payments]
  );

  const filtersActive =
    !!searchQuery ||
    methodFilter !== "all" ||
    categoryFilter !== "all" ||
    sortBy !== "date_desc";

  const displayedPayments = useMemo(() => {
    let list = payments.slice();

    // 1. Búsqueda por nombre
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => getUserName(p).toLowerCase().includes(q));
    }

    // 2. Filtro por método
    if (methodFilter !== "all") {
      list = list.filter((p) => p.payment_method === methodFilter);
    }

    // 3. Filtro por categoría
    if (categoryFilter !== "all") {
      if (categoryFilter === ARTICLE_SALE_KEY) {
        list = list.filter((p) => p.is_article_sale === true);
      } else {
        list = list.filter((p) => {
          if (!p.description) return false;
          return p.description
            .split(", ")
            .map((c) => c.trim())
            .includes(categoryFilter);
        });
      }
    }

    // 4. Orden (siempre se aplica; default = fecha desc)
    list.sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return getPaymentDate(a).getTime() - getPaymentDate(b).getTime();
        case "amount_desc":
          return Number(b.amount) - Number(a.amount);
        case "amount_asc":
          return Number(a.amount) - Number(b.amount);
        case "date_desc":
        default:
          return getPaymentDate(b).getTime() - getPaymentDate(a).getTime();
      }
    });

    return list;
  }, [payments, searchQuery, methodFilter, categoryFilter, sortBy]);

  const totalPages = Math.ceil(displayedPayments.length / pageSize);
  const paginatedPayments = displayedPayments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, displayedPayments.length);

  const clearFilters = () => {
    setSearchQuery("");
    setMethodFilter("all");
    setCategoryFilter("all");
    setSortBy("date_desc");
  };


  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const handleDeletePayment = async (paymentId: string) => {
    try {
      const { error } = await supabase
        .from("payments")
        .delete()
        .eq("id", paymentId);

      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["payments", gymId] }),
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey?.[0];
            return k === "arqueo-payments" || k === "arqueo-closure";
          },
        }),
      ]);
      toast({
        title: "Pago eliminado",
        description: "El pago se eliminó y el arqueo se actualizó.",
      });
    } catch (error: any) {
      console.error("Error deleting payment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el pago.",
      });
    }
  };

  const handleDeleteAllPayments = async () => {
    try {
      let query = supabase
        .from("payments")
        .delete();

      if (gymId) {
        query = query.eq("profiles.gym_id", gymId);
      }

      const { error } = await query;

      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["payments", gymId] }),
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey?.[0];
            return k === "arqueo-payments" || k === "arqueo-closure";
          },
        }),
      ]);
      toast({
        title: "Pagos eliminados",
        description: "Todos los pagos han sido eliminados correctamente.",
      });
    } catch (error: any) {
      console.error("Error deleting all payments:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron eliminar los pagos.",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2 mb-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-full sm:w-[170px]">
            <SelectValue placeholder="Método de pago" />
          </SelectTrigger>
          <SelectContent className="bg-black/70 backdrop-blur-md">
            <SelectItem value="all">Todos los métodos</SelectItem>
            {availableMethods.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent className="bg-black/70 backdrop-blur-md">
            <SelectItem value="all">Todas las categorías</SelectItem>
            {hasArticleSales && (
              <SelectItem value={ARTICLE_SALE_KEY}>Venta de artículo</SelectItem>
            )}
            {availableCategories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Ordenar por" />
          </SelectTrigger>
          <SelectContent className="bg-black/70 backdrop-blur-md">
            <SelectItem value="date_desc">Fecha: más reciente</SelectItem>
            <SelectItem value="date_asc">Fecha: más antigua</SelectItem>
            <SelectItem value="amount_desc">Monto: mayor a menor</SelectItem>
            <SelectItem value="amount_asc">Monto: menor a mayor</SelectItem>
          </SelectContent>
        </Select>

        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="gap-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
            Limpiar filtros
          </Button>
        )}
      </div>

      {isMobile ? (
        <div className="space-y-2">
          {paginatedPayments.map((payment) => (
            <div
              key={payment.id}
              className="rounded-lg border border-border bg-card/60 backdrop-blur p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-foreground truncate">
                    {getUserName(payment)}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {formatDateShort(payment)}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 flex items-start gap-1">
                  <div className="font-bold text-foreground tabular-nums whitespace-nowrap">
                    ${Number(payment.amount).toLocaleString()}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 -mr-1">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          toast({
                            title: "Categorías de pago",
                            description: payment.description || "Sin descripción",
                          });
                        }}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Ver categorías
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditingPayment(payment)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Eliminar
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción no se puede deshacer. Se eliminará el registro de pago seleccionado.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeletePayment(payment.id)}>
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-1 items-center">
                {payment.description ? (
                  <>
                    {payment.is_article_sale && (
                      <ShoppingBag className="h-3.5 w-3.5 text-amber-600" />
                    )}
                    {payment.description.split(', ').map((category, index) => (
                      <span
                        key={index}
                        className={
                          payment.is_article_sale
                            ? "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                            : "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary border border-primary/20"
                        }
                      >
                        {category}
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Sin descripción</span>
                )}
              </div>

              {payment.payment_method && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary text-secondary-foreground border border-border">
                    {payment.payment_method}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[80px]">Usuario</TableHead>
                <TableHead className="min-w-[70px]">Fecha</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="min-w-[100px]">Método</TableHead>
                <TableHead className="text-right min-w-[80px] whitespace-nowrap">Monto</TableHead>
                <TableHead className="text-right min-w-[100px] w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPayments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell className="min-w-[80px]">{getUserName(payment)}</TableCell>
                  <TableCell className="min-w-[70px] whitespace-nowrap">
                    {formatDate(payment)}
                  </TableCell>
                  <TableCell>
                    {payment.description ? (
                      <div className="flex flex-wrap gap-1 items-center">
                        {payment.is_article_sale && (
                          <ShoppingBag className="h-3.5 w-3.5 text-amber-600" />
                        )}
                        {payment.description.split(', ').map((category, index) => (
                          <span
                            key={index}
                            className={
                              payment.is_article_sale
                                ? "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                                : "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                            }
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Sin descripción</span>
                    )}
                  </TableCell>
                  <TableCell className="min-w-[100px]">
                    {payment.payment_method ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                        {payment.payment_method}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right min-w-[80px] whitespace-nowrap">
                    ${Number(payment.amount).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right min-w-[100px] w-[100px]">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEditingPayment(payment)}
                        title="Editar pago"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Eliminar pago">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción no se puede deshacer. Se eliminará el registro de pago seleccionado.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeletePayment(payment.id)}>
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-2">
          <p className="text-sm text-muted-foreground">
            Mostrando {startRecord}-{endRecord} de {displayedPayments.length} registros
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <EditPaymentDialog
        payment={editingPayment}
        gymId={gymId}
        onClose={() => setEditingPayment(null)}
      />
    </div>
  );
};